import {ErrorHandler, inject, LOCALE_ID, NgModule, provideEnvironmentInitializer} from '@angular/core';
import {registerLocaleData} from '@angular/common';
import localePt from '@angular/common/locales/pt';
import {BrowserModule} from '@angular/platform-browser';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {ExtraOptions, RouterModule} from '@angular/router';
import {TranslateModule} from '@ngx-translate/core';
import {CKEditorModule} from 'ng2-ckeditor';
import 'hammerjs';

import {cdkConfig} from 'app/cdk-config';

import {AppComponent} from './app.component';
import {AppStoreModule} from 'app/store/store.module';
import {LayoutModule} from 'app/layout/layout.module';
import {AuthGuard} from './main/guard';
import {LoginInterceptor} from './main/auth/login/login.interceptor';
import {LogoutInterceptor} from './main/auth/login/logout.interceptor';

import {ModelModule} from '@cdk/models';
import {ErrorInterceptor} from './main/auth/login/error.interceptor';
import {LoginStoreModule} from './main/auth/login/store/store.module';
import {MatDialogModule} from '@angular/material/dialog';

import {MatStepperIntl} from '@angular/material/stepper';
import {CdkMatStepperIntl} from '../@cdk/angular/cdk-mat-stepper-intl';
import {MatPaginatorIntl} from '@angular/material/paginator';
import {CdkMatPaginatorIntl} from '../@cdk/angular/cdk-mat-paginator-intl';
import {GlobalErrorHandler} from './global-error-handler';
import {AssinaturaService} from '../@cdk/services/assinatura.service';
import {DocumentoService} from '../@cdk/services/documento.service';
import {AvaliacaoService} from '../@cdk/services/avaliacao.service';
import {ObjetoAvaliadoService} from '../@cdk/services/objeto-avaliado.service';
import {modulesConfig} from '../modules/modules-config';
import {CdkSplashScreenService} from '../@cdk/services/splash-screen.service';
import {ChatBotModule} from 'app/layout/components/chat-bot/chat-bot.module';
import {ComponenteDigitalService} from '../@cdk/services/componente-digital.service';
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import {providePrimeNG} from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import {Settings} from 'luxon';
import {
    LuxonDateAdapter,
    MAT_LUXON_DATE_ADAPTER_OPTIONS,
    MAT_LUXON_DATE_FORMATS,
    MatLuxonDateModule,
} from '@angular/material-luxon-adapter';
import {MAT_DATE_FORMATS, DateAdapter, MAT_DATE_LOCALE} from '@angular/material/core';
import {MatDatepickerIntl} from '@angular/material/datepicker';
import {CdkMatDatepickerIntl} from '../@cdk/angular/cdk-mat-datepicker-intl';
import {CdkSidebarComponent} from "../@cdk/components/sidebar/sidebar.component";
import {CdkThemeOptionsComponent} from "../@cdk/components/theme-options/theme-options.component";
import {CdkProgressBarComponent} from "../@cdk/components/progress-bar/progress-bar.component";
import { CdkModule } from '@cdk/cdk.module';
import {provideEnvironmentNgxCurrency} from "ngx-currency";
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatSnackBarModule} from '@angular/material/snack-bar';
import {VisualizadorService} from '../@cdk/services/visualizador.service';
import {JuntadaService} from '../@cdk/services/juntada.service';
import {BookmarkService} from '../@cdk/services/bookmark.service';
import {VolumeService} from '../@cdk/services/volume.service';
import {ProcessoService} from '../@cdk/services/processo.service';
import {VinculacaoEtiquetaService} from "../@cdk/services/vinculacao-etiqueta.service";
import {VinculacaoDocumentoService} from "../@cdk/services/vinculacao-documento.service";
import {DesentranhamentoService} from "../@cdk/services/desentranhamento.service";
import {TriagemService} from "../@cdk/services/triagem.service";
import {DadosDadosFormularioService} from "../@cdk/services/dados-formulario.service";
import {DocumentoIAMetadataService} from "../@cdk/services/documento-ia-metadata.service";
import {QuillModule} from 'ngx-quill';
import Counter from '../@cdk/components/quill/counter';
import { DynamicDialogModule, DialogService } from 'primeng/dynamicdialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import {TarefaService} from '../@cdk/services/tarefa.service';
import {TramitacaoService} from '../@cdk/services/tramitacao.service';

registerLocaleData(localePt, 'pt');
Settings.defaultLocale = 'pt-BR';
Settings.defaultZone = 'America/Sao_Paulo';

let routes = [
    {
        path: 'apps',
        loadChildren: (): any => import('./main/apps/apps.module').then(m => m.AppsModule),
        canActivate: [AuthGuard]
    },
    {
        path        : 'documento',
        loadChildren: () => import('./main/apps/componente-digital/componente-digital-link.module').then(m => m.ComponenteDigitalLinkModule),
        canActivate: [AuthGuard]
    },
    {
        path        : 'documento-externo',
        loadChildren: () => import('./main/apps/documento-externo/documento-externo.module').then(m => m.DocumentoExternoModule),
        canActivate: [AuthGuard]
    },
    {
        path        : 'acesso-negado',
        loadChildren: () => import('./main/apps/componente-digital/componente-digital-acesso-negado/componente-digital-acesso-negado.module').then(m => m.ComponenteDigitalAcessoNegadoModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'auth',
        loadChildren: (): any => import('./main/auth/auth.module').then(m => m.AuthModule)
    },
    {
        path: 'pages',
        loadChildren: (): any => import('./main/pages/pages.module').then(m => m.PagesModule)
    },
    {
        path: 'protocolo',
        loadChildren: (): any => import('./main/protocolo/protocolo.module').then(m => m.ProtocoloModule)
    },
    {
        path: 'retornoNeoId',
        loadChildren: (): any => import('./main/neoid/neoid.module').then(m => m.NeoIdModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'formulario',
        loadChildren: (): any => import('./main/formulario/formulario.module').then(m => m.FormularioModule),
    },
    {
        path: '**',
        redirectTo: 'auth/login'
    }
];

const routingConfiguration: ExtraOptions = {
    paramsInheritanceStrategy: 'always',
    anchorScrolling: 'enabled'
//    onSameUrlNavigation: 'reload'
};

let httpInterceptors = [
    {provide: HTTP_INTERCEPTORS, useClass: LoginInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: LogoutInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true},
];

let rootModules = [];
let childModules = [];

const path = 'app';
modulesConfig.forEach((module: any) => {
    if (module.hasOwnProperty('extension')) {
        const extension = new module.extension();

        rootModules = [
            ...rootModules,
            extension.forRoot()
        ];
        childModules = [
            ...childModules,
            extension.forChild(path)
        ];
        routes = extension.manageRoutes(path, routes);
        httpInterceptors = extension.manageInterceptors(path, httpInterceptors);
    }

    if (module.routes.hasOwnProperty(path)) {
        module.routes[path].forEach((r => routes.unshift(r)));
    }
});

@NgModule({
    declarations: [
        AppComponent
    ],
    bootstrap: [
        AppComponent
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        RouterModule.forRoot(routes, routingConfiguration),
        TranslateModule.forRoot(),
        // Material
        MatButtonModule,
        MatIconModule,
        MatSnackBarModule,
        MatDialogModule,
        DynamicDialogModule,
        ConfirmDialogModule,
        // Cdk modules
        CdkModule.forRoot(cdkConfig),
        CKEditorModule,
        // InMemoryWebApiModule.forRoot(FakeDbService, {
        //     delay: 0,
        //     passThruUnknownUrl: true
        // }),
        // App modules
        LayoutModule,
        AppStoreModule,
        LoginStoreModule,
        MatLuxonDateModule,
        ModelModule,

        // Google chat bot
        ChatBotModule,

        ...rootModules,
        ...childModules,
        CdkSidebarComponent,
        CdkThemeOptionsComponent,
        CdkProgressBarComponent,
        QuillModule.forRoot({
            customModules: [{
                implementation: Counter,
                path: 'modules/counter'
            }]
        }),
    ],
    providers: [
        provideAnimationsAsync(),
        providePrimeNG({
            theme: {
                preset: Aura,
                options: {
                    darkModeSelector: '.dark',
                    prefix: 'p',
                    cssLayer: {
                        name: 'primeng',
                        order: 'tailwind-base, primeng, tailwind-utilities'
                    }
                }
            }
        }),
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        ...httpInterceptors,
        provideEnvironmentInitializer(() => inject(CdkSplashScreenService)),
        { provide: MatStepperIntl, useClass: CdkMatStepperIntl },
        { provide: MatPaginatorIntl, useClass: CdkMatPaginatorIntl },
        { provide: MatDatepickerIntl, useClass: CdkMatDatepickerIntl },
        { provide: LOCALE_ID, useValue: 'pt' },
        { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
        { provide: AssinaturaService, useClass: AssinaturaService },
        { provide: BookmarkService, useClass: BookmarkService },
        { provide: DesentranhamentoService, useClass: DesentranhamentoService },
        { provide: JuntadaService, useClass: JuntadaService },
        { provide: DocumentoService, useClass: DocumentoService },
        { provide: AvaliacaoService, useClass: AvaliacaoService },
        { provide: ObjetoAvaliadoService, useClass: ObjetoAvaliadoService },
        { provide: ComponenteDigitalService, useClass: ComponenteDigitalService },
        { provide: VisualizadorService, useClass: VisualizadorService },
        { provide: VinculacaoDocumentoService, useClass: VinculacaoDocumentoService },
        { provide: VinculacaoEtiquetaService, useClass: VinculacaoEtiquetaService },
        { provide: TriagemService, useClass: TriagemService },
        { provide: DadosDadosFormularioService, useClass: DadosDadosFormularioService },
        { provide: DocumentoIAMetadataService, useClass: DocumentoIAMetadataService },
        { provide: TarefaService, useClass: TarefaService },
        { provide: VolumeService, useClass: VolumeService },
        { provide: ProcessoService, useClass: ProcessoService },
        { provide: DialogService, useClass: DialogService },
        { provide: ConfirmationService, useClass: ConfirmationService },
        { provide: TramitacaoService, useClass: TramitacaoService },
        { provide: MAT_LUXON_DATE_ADAPTER_OPTIONS, useValue: {useUtc: false, firstDayOfWeek: 0} },
        { provide: DateAdapter, useClass: LuxonDateAdapter, deps: [MAT_DATE_LOCALE, MAT_LUXON_DATE_ADAPTER_OPTIONS] },
        { provide: MAT_DATE_FORMATS, useValue: MAT_LUXON_DATE_FORMATS },
        provideHttpClient(withInterceptorsFromDi()),
        provideEnvironmentNgxCurrency({
            align: "left",
            decimal: ",",
            prefix: "R$ ",
            thousands: ".",
        })
    ]
})
export class AppModule {
}
